import type { FlightPlannerCopy } from './planner_copy.js';
import type { PlannerHeatView } from './heat_view.js';
import type { PlannerLeg } from './planner_models.js';
import { skirtBest } from './heat_savings.js';
import { searchCtaLabel } from './planner_ctas.js';
import { daysBetween } from '@voll-intelligence/dates';
import { fmtDayMonth, fmtTripRange, fmtWeekdayDayMonth } from './planner_dates.js';
import type { PlannerState } from './planner_state.js';
import type { BinaryPlanner } from './binary_planner.js';

/*
 * Os rótulos derivados do estado do planner (a frase da saia, o QUANDO, os
 * CTAs). Saíram de `planner_form.tsx` pelo teto de linhas dele.
 */

/**
 * A frase da ESQUERDA da saia: o estado da escolha. É a razão de o título ter
 * saído — a rota já está nos campos DE/PARA, e o que o card precisava dizer e não
 * dizia era o que falta escolher.
 */
function choiceLine(
  p: { routeOk: boolean; out?: string; ret?: string; oneWay: boolean },
  copy: FlightPlannerCopy,
  locale: string,
): string {
  if (!p.routeOk) return copy.calDisabledHint;
  if (!p.out) return copy.stateNeedsOut;
  if (p.ret) {
    const nights = daysBetween(p.out, p.ret);
    return copy.stateRange({
      from: fmtWeekdayDayMonth(p.out, locale, []),
      to: fmtWeekdayDayMonth(p.ret, locale, [p.out, p.ret]),
      nights,
    });
  }
  const date = fmtWeekdayDayMonth(p.out, locale);
  return p.oneWay ? copy.stateOneWay({ date }) : copy.stateNeedsRet({ date });
}

/**
 * O intervalo da viagem MULTI na linha colapsada: a data do 1º trecho até a do
 * último. Uma data só quando é a mesma (ou quando só o 1º trecho tem data);
 * `undefined` enquanto nenhuma data foi escolhida — aí a linha mostra só a cadeia,
 * porque um intervalo de "a definir a a definir" não informa nada.
 */
export function legsRangeLabel(legs: PlannerLeg[], locale: string): string | undefined {
  const from = legs[0]?.date;
  const to = legs.at(-1)?.date;
  if (!from) return undefined;
  if (!to || to === from) return fmtWeekdayDayMonth(from, locale);
  return `${fmtWeekdayDayMonth(from, locale, [])} – ${fmtWeekdayDayMonth(to, locale, [from, to])}`;
}

export function plannerLabels({
  state,
  binary,
  heat,
  submitLabel,
  band,
  collapsed,
  naked,
  copy,
  locale,
}: {
  state: PlannerState;
  binary: BinaryPlanner;
  heat: PlannerHeatView;
  submitLabel?: string;
  band: boolean;
  collapsed: boolean;
  /** NAKED (#2167): o CTA inline é pílula — o default longo não cabe nela.
   *  Só vale no aberto: `searchCtaLabel` apara band/collapsed. */
  naked: boolean | undefined;
  copy: FlightPlannerCopy;
  locale: string;
}) {
  const choice = choiceLine(
    { routeOk: binary.routeOk, out: state.out, ret: state.ret, oneWay: binary.oneWayTab },
    copy,
    locale,
  );
  const best = skirtBest({ heat, binary, copy, locale });
  // O CTA de CONFIRMAR (popover da faixa e colapsado) nomeia o par quando ele
  // está fechado ("Confirmar 11–20 ago", #2106) — o rótulo neutro fica pra
  // escolha ainda aberta. O CTA de BUSCAR do card segue fixo: lá quem nomeia o
  // par é a saia ("{ida} → {volta} · N noites").
  const confirmDatesLabel =
    state.out && state.ret
      ? // O par compactado do CTA ("Confirmar 15–22 set", #2106): a faixa das pills,
        // porque o botão é curto demais pro par por extenso do `whenLabel`.
        copy.confirmRange({ range: fmtTripRange(state.out, state.ret, locale) })
      : copy.confirmDates;
  const ctaLabel =
    submitLabel ?? (collapsed ? confirmDatesLabel : searchCtaLabel(naked, band, collapsed, copy));
  let whenLabel: string;
  if (!state.out) {
    whenLabel = copy.whenEmpty;
  } else if (state.ret) {
    whenLabel = fmtTripRange(state.out, state.ret, locale);
  } else {
    whenLabel = fmtDayMonth(state.out, locale);
  }
  // A meia-resposta do ida e volta (#2136): com a ida escolhida e a volta não, o
  // campo mostra "11 ago – Volta" com o que falta em tom de vazio — fingir campo
  // completo escondia justamente a metade pendente (o `resumoComVoltaPendente`
  // do desenho). `pickingReturn` já é essa fase.
  const whenPendingTail = binary.pickingReturn ? copy.whenReturnPending : undefined;
  return { choice, best, ctaLabel, confirmDatesLabel, whenLabel, whenPendingTail };
}
